import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const SizeGuide = () => {
  const [activeCategory, setActiveCategory] = useState('women-indian');

  const sizeData = {
    'women-indian': {
      title: "Women's Indian Wear Size Guide",
      subtitle: "Sarees, Kurtis, Lehengas, Salwar Suits",
      measurements: [
        { size: 'XS', bust: '30-32', waist: '24-26', hips: '32-34', length: '38-40' },
        { size: 'S', bust: '32-34', waist: '26-28', hips: '34-36', length: '38-40' },
        { size: 'M', bust: '34-36', waist: '28-30', hips: '36-38', length: '40-42' },
        { size: 'L', bust: '36-38', waist: '30-32', hips: '38-40', length: '40-42' },
        { size: 'XL', bust: '38-40', waist: '32-34', hips: '40-42', length: '42-44' },
        { size: 'XXL', bust: '40-42', waist: '34-36', hips: '42-44', length: '42-44' },
      ],
      tips: [
        'All measurements are in inches',
        'For Sarees, blouse measurements are standard; saree draping is one-size-fits-most',
        'For Kurtis, length is from shoulder to hem',
        'If between sizes, size up for comfort',
        'Custom tailoring available on request'
      ]
    },
    'women-western': {
      title: "Women's Western Wear Size Guide",
      subtitle: "Tops, Dresses, Jeans, Jackets",
      measurements: [
        { size: 'XS', bust: '30-32', waist: '24-26', hips: '33-35', inseam: '29-30' },
        { size: 'S', bust: '32-34', waist: '26-28', hips: '35-37', inseam: '30-31' },
        { size: 'M', bust: '34-36', waist: '28-30', hips: '37-39', inseam: '30-31' },
        { size: 'L', bust: '36-39', waist: '30-33', hips: '39-42', inseam: '31-32' },
        { size: 'XL', bust: '39-42', waist: '33-36', hips: '42-45', inseam: '31-32' },
        { size: 'XXL', bust: '42-45', waist: '36-39', hips: '45-48', inseam: '32-33' },
      ],
      tips: [
        'All measurements are in inches',
        'Inseam is for pants/jeans - from crotch to ankle',
        'For dresses, refer to bust and waist measurements',
        'Jackets: measure across shoulders for best fit',
        'If between sizes, order your usual size'
      ]
    },
    'men-indian': {
      title: "Men's Indian Wear Size Guide",
      subtitle: "Kurtas, Sherwanis, Nehru Jackets",
      measurements: [
        { size: 'S', chest: '36-38', waist: '30-32', shoulder: '16-16.5', length: '40-42' },
        { size: 'M', chest: '38-40', waist: '32-34', shoulder: '16.5-17', length: '42-44' },
        { size: 'L', chest: '40-42', waist: '34-36', shoulder: '17-17.5', length: '44-46' },
        { size: 'XL', chest: '42-44', waist: '36-38', shoulder: '17.5-18', length: '44-46' },
        { size: 'XXL', chest: '44-46', waist: '38-40', shoulder: '18-18.5', length: '46-48' },
      ],
      tips: [
        'All measurements are in inches',
        'Chest: measure around fullest part',
        'Length: from shoulder to hem',
        'Kurtas have relaxed fit - size up for looser drape',
        'Sherwanis are tailored - accurate measurements critical'
      ]
    }
  };

  const howToMeasure = [
    {
      title: 'Bust/Chest',
      description: 'Measure around the fullest part of your bust/chest, keeping the tape horizontal.',
      icon: '👕'
    },
    {
      title: 'Waist',
      description: 'Measure around your natural waistline (the narrowest part of your torso).',
      icon: '📏'
    },
    {
      title: 'Hips',
      description: 'Measure around the fullest part of your hips, keeping the tape parallel to the floor.',
      icon: '👖'
    },
    {
      title: 'Length',
      description: 'For tops: measure from highest point of shoulder straight down. For pants: inside leg from crotch to ankle.',
      icon: '📐'
    }
  ];

  const currentData = sizeData[activeCategory];

  return (
    <>
      <SEO
        title="Size Guide - House of Deepak"
        description="Find your perfect fit with our comprehensive size guide for Indian and Western wear. Detailed measurements for women's and men's clothing."
        keywords="size guide, size chart, clothing measurements, Indian wear sizes, Western wear sizes, how to measure"
      />
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="section-padding">
          <div className="container-max max-w-6xl">
            {/* Header */}
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-4xl md:text-5xl font-serif font-light text-deep-brown mb-4">
                Size Guide
              </h1>
              <p className="text-lg text-warm-gray max-w-2xl mx-auto">
                Find your perfect fit with our comprehensive size charts and measurement guide
              </p>
            </motion.div>

            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {Object.keys(sizeData).map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-6 py-3 rounded-md font-medium transition-all duration-300 ${
                    activeCategory === category
                      ? 'bg-deep-brown text-white shadow-md'
                      : 'bg-white text-deep-brown border border-warm-gray/30 hover:border-deep-brown'
                  }`}
                >
                  {sizeData[category].title.split("'s ")[0]}'s{' '}
                  {category.includes('indian') ? 'Indian' : 'Western'}
                </button>
              ))}
            </div>

            {/* Size Chart */}
            <motion.div
              key={activeCategory}
              className="bg-white rounded-lg shadow-sm border border-warm-gray/20 p-6 md:p-8 mb-8"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h2 className="text-2xl font-serif text-deep-brown mb-2">
                {currentData.title}
              </h2>
              <p className="text-warm-gray mb-6">{currentData.subtitle}</p>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm md:text-base">
                  <thead>
                    <tr className="border-b-2 border-deep-brown">
                      <th className="text-left py-3 px-2 text-deep-brown font-semibold">Size</th>
                      {Object.keys(currentData.measurements[0])
                        .filter(key => key !== 'size')
                        .map(key => (
                          <th key={key} className="text-center py-3 px-2 text-deep-brown font-semibold capitalize">
                            {key} (in)
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.measurements.map((row, idx) => (
                      <tr key={idx} className="border-b border-warm-gray/20 hover:bg-cream/50">
                        <td className="py-3 px-2 font-medium text-deep-brown">{row.size}</td>
                        {Object.entries(row)
                          .filter(([key]) => key !== 'size')
                          .map(([key, value]) => (
                            <td key={key} className="text-center py-3 px-2 text-warm-gray">
                              {value}
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Tips */}
              <div className="mt-6 bg-sage/10 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-deep-brown mb-3">Sizing Tips:</h3>
                <ul className="space-y-2">
                  {currentData.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start text-sm text-warm-gray">
                      <span className="text-sage mr-2 mt-0.5">✓</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* How to Measure */}
            <div className="bg-white rounded-lg shadow-sm border border-warm-gray/20 p-6 md:p-8">
              <h2 className="text-2xl font-serif text-deep-brown mb-6 text-center">
                How to Measure Yourself
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {howToMeasure.map((item, idx) => (
                  <motion.div
                    key={idx}
                    className="text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <div className="text-4xl mb-3">{item.icon}</div>
                    <h3 className="text-lg font-semibold text-deep-brown mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-warm-gray">
                      {item.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Additional Tips */}
              <div className="mt-8 p-4 bg-gold/10 rounded-lg border border-gold/30">
                <p className="text-sm text-deep-brown text-center">
                  <strong>Pro Tip:</strong> Measure yourself over light clothing for accuracy. 
                  If you're between sizes, we recommend sizing up for a more comfortable fit. 
                  For custom tailoring or specific questions, contact our support team.
                </p>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="text-center mt-10">
              <p className="text-warm-gray mb-4">Still unsure about sizing?</p>
              <a
                href="/contact"
                className="inline-block bg-deep-brown text-white px-8 py-3 rounded-md font-medium hover:bg-warm-gray transition-colors duration-300"
              >
                Contact Us for Help
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SizeGuide;
